
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { X, History, Download } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Node, Edge } from '@xyflow/react';

interface Version {
  id: string;
  version_name: string | null;
  created_at: string;
  is_auto_save: boolean;
  nodes_data: Node[];
  edges_data: Edge[];
}

interface VersionHistoryProps {
  whiteboardId: string;
  onClose: () => void;
  onLoadVersion: (nodes: Node[], edges: Edge[]) => void;
}

export const VersionHistory: React.FC<VersionHistoryProps> = ({
  whiteboardId,
  onClose,
  onLoadVersion
}) => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVersions();
  }, [whiteboardId]);

  const loadVersions = async () => {
    try {
      const { data, error } = await supabase
        .from('whiteboard_versions')
        .select('*')
        .eq('whiteboard_id', whiteboardId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      
      // Transform the data to match our Version interface with proper type safety
      const transformedVersions: Version[] = (data || []).map(item => ({
        id: item.id,
        version_name: item.version_name,
        created_at: item.created_at,
        is_auto_save: item.is_auto_save || false,
        nodes_data: Array.isArray(item.nodes_data) ? item.nodes_data as unknown as Node[] : [],
        edges_data: Array.isArray(item.edges_data) ? item.edges_data as unknown as Edge[] : []
      }));
      
      setVersions(transformedVersions);
    } catch (error) {
      console.error('Error loading versions:', error);
      toast({
        title: "Error",
        description: "Failed to load version history",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadVersion = (version: Version) => {
    onLoadVersion(version.nodes_data, version.edges_data);
    toast({
      title: "Version Loaded",
      description: `Loaded ${version.version_name || 'auto-saved version'} from ${new Date(version.created_at).toLocaleString()}`,
    });
    onClose();
  };

  return (
    <Card className="fixed right-4 top-20 w-80 max-h-96 z-50 bg-white border shadow-lg">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4" />
            <h3 className="font-semibold">Version History</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-2 max-h-64 overflow-y-auto">
          {loading ? (
            <div className="text-sm text-gray-500">Loading versions...</div>
          ) : versions.length === 0 ? (
            <div className="text-sm text-gray-500">No versions found</div>
          ) : (
            versions.map((version) => (
              <div key={version.id} className="p-2 bg-gray-50 rounded hover:bg-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-sm">
                      {version.version_name || (version.is_auto_save ? 'Auto-save' : 'Manual save')}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(version.created_at).toLocaleString()}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => loadVersion(version)}
                  >
                    <Download className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Card>
  );
};
